import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarDays, List, ListTodo } from 'lucide-react';
import { z } from 'zod';
import { WORK_LANES, listWorkItems } from '@mkcore/platform/schema';
import { Button, Card, EmptyState, ErrorState, PageHeader, Skeleton, cn } from '@mkcore/ui';
import { call } from '@/lib/api';
import { useErrorMessage } from '@/lib/mutation';
import { useShortcuts } from '@/lib/shortcuts';
import { snooze, snoozable, snoozeChoices, type Item } from '@/features/work/actions';
import { AgendaView } from '@/features/work/agenda';
import { ICONS, WorkActions, WorkMeta, WorkPanel, useWorkActions, workTitle } from '@/features/work/items';

const search = z.object({
  lane: z.enum(WORK_LANES).optional(),
  view: z.enum(['list', 'agenda']).optional(),
  date: z.string().optional(),
  range: z.enum(['day', 'week']).optional(),
  item: z.string().optional().describe('type:id of the work item in the side panel (CO-1387)'),
});
export const Route = createFileRoute('/_app/werk')({ validateSearch: search, component: WerkPage });

export const workQuery = { queryKey: ['work', 'item', 'list'], queryFn: () => call(listWorkItems, { limit: 200 }) };
const refOf = (i: Item) => `${i.type}:${i.id}`;

/**
 * Werk (CO-1386, ADR-0038): one queue for everything that waits on you, from all domains, in four lanes. Every row has
 * its primary action as a button and the rest in a menu; a click opens the side panel, Ctrl-click or o the full page.
 * Keys: j and k move, Enter opens the panel, o the page, a runs the primary action, s snoozes until tomorrow.
 */
function WerkPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const message = useErrorMessage();
  const q = useQuery(workQuery);
  const lane = s.lane ?? 'needs_me';
  const view = s.view ?? 'list';
  const all = q.data?.items ?? [];
  const items = useMemo(() => all.filter((i) => i.lane === lane), [all, lane]);
  const open = s.item ? (all.find((i) => refOf(i) === s.item) ?? null) : null;
  const [focus, setFocus] = useState(0);
  const go = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const count = (l: (typeof WORK_LANES)[number]) => all.filter((i) => i.lane === l).length;

  useEffect(() => setFocus((f) => Math.min(f, Math.max(items.length - 1, 0))), [items.length]);
  useEffect(() => {
    if (view === 'list') document.getElementById(`work-row-${focus}`)?.focus({ preventScroll: false });
  }, [focus, view]);

  const current = items[focus];
  const shortcuts = useMemo(
    () => [
      { keys: 'j', run: () => setFocus((f) => Math.min(f + 1, items.length - 1)) },
      { keys: 'k', run: () => setFocus((f) => Math.max(f - 1, 0)) },
      { keys: 'o', run: () => current && router.history.push(current.href) },
      { keys: 's', run: () => current && snoozable(current) && void snooze(current, snoozeChoices()[1]!.at).then(() => void q.refetch()) },
    ],
    [items.length, current, router, q],
  );
  useShortcuts(shortcuts, view === 'list' && !open);

  return (
    <>
      <PageHeader
        title={t('work.title')}
        description={t('work.description')}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant={view === 'list' ? 'primary' : 'secondary'} onClick={() => go({ view: undefined })}>
              <List /> {t('work.views.list')}
            </Button>
            <Button size="sm" variant={view === 'agenda' ? 'primary' : 'secondary'} onClick={() => go({ view: 'agenda' })}>
              <CalendarDays /> {t('work.views.agenda')}
            </Button>
          </div>
        }
      />
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-64" />
      ) : view === 'agenda' ? (
        <AgendaView items={all} date={s.date} range={s.range ?? 'day'} onChange={go} onOpen={(i) => go({ item: refOf(i) })} />
      ) : (
        <>
          <nav aria-label={t('work.lanesLabel')} className="mb-3 flex flex-wrap gap-1 border-b border-line-subtle">
            {WORK_LANES.map((l) => (
              <button
                key={l}
                type="button"
                aria-current={l === lane ? 'page' : undefined}
                className={cn('-mb-px border-b-2 px-3 py-2 text-sm', l === lane ? 'border-primary font-bold text-fg-1' : 'border-transparent text-fg-2 hover:text-fg-1')}
                onClick={() => {
                  setFocus(0);
                  go({ lane: l === 'needs_me' ? undefined : l });
                }}
              >
                {t(`work.lanes.${l}`)} <span className="tabular text-xs text-fg-3">{count(l)}</span>
              </button>
            ))}
          </nav>
          {q.data.failed.length > 0 && (
            <p role="status" className="mb-2 text-sm text-warning">
              {t('work.failed', { types: q.data.failed.map((f) => t(`work.types.${f}`, { defaultValue: f })).join(', ') })}
            </p>
          )}
          {!items.length ? (
            <EmptyState title={t(`work.empty.${lane}`)} />
          ) : (
            <Lane lane={lane} items={items} focus={focus} onFocus={setFocus} onOpen={(i) => go({ item: refOf(i) })} />
          )}
        </>
      )}
      <WorkPanel item={open} onClose={() => go({ item: undefined })} />
    </>
  );
}

function Lane({ lane, items, focus, onFocus, onOpen }: { lane: string; items: Item[]; focus: number; onFocus: (n: number) => void; onOpen: (i: Item) => void }) {
  const { t } = useTranslation();
  const index = new Map(items.map((i, n) => [i, n]));
  // Todo is split by when it is due; the other lanes are one list.
  const groups = lane === 'todo' ? (['today', 'week', 'later'] as const).map((b) => ({ key: b, items: items.filter((i) => i.bucket === b) })).filter((g) => g.items.length) : [{ key: null, items }];
  return (
    <div className="grid gap-4">
      {groups.map((g) => (
        <Card key={g.key ?? 'all'} className="p-2">
          <section aria-label={g.key ? t(`work.buckets.${g.key}`) : t(`work.lanes.${lane}`)}>
            {g.key && (
              <h2 className="px-2 pt-1 text-sm font-bold">
                {t(`work.buckets.${g.key}`)} <span className="font-normal text-fg-3">{g.items.length}</span>
              </h2>
            )}
            <ul className="divide-y divide-line-subtle">
              {g.items.map((i) => (
                <Row key={refOf(i)} item={i} n={index.get(i)!} focused={index.get(i) === focus} onFocus={onFocus} onOpen={onOpen} />
              ))}
            </ul>
          </section>
        </Card>
      ))}
    </div>
  );
}

function Row({ item, n, focused, onFocus, onOpen }: { item: Item; n: number; focused: boolean; onFocus: (n: number) => void; onOpen: (i: Item) => void }) {
  const { t } = useTranslation();
  const router = useRouter();
  const { start, dialog } = useWorkActions(item);
  const Icon = ICONS[item.type] ?? ListTodo;
  return (
    <li
      id={`work-row-${n}`}
      tabIndex={focused ? 0 : -1}
      aria-label={item.title}
      className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2 py-2 text-sm hover:bg-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onFocus={() => onFocus(n)}
      onClick={(e) => (e.ctrlKey || e.metaKey ? router.history.push(item.href) : onOpen(item))}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter') onOpen(item);
        else if (e.key === 'a' && item.primary) {
          e.preventDefault();
          start(item.primary);
        }
      }}
    >
      <Icon className="size-4 shrink-0 text-fg-3" aria-label={t(`work.types.${item.type}`, { defaultValue: item.type })} />
      <span className="grid min-w-0 flex-1">
        <span className="truncate font-bold">{workTitle(item, t)}</span>
        <WorkMeta item={item} />
      </span>
      <WorkActions item={item} />
      {dialog}
    </li>
  );
}
